import { queryOptions, useQueries } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { useState } from "react";

import { BANK_STATEMENTS_EXTRACTION_STATUS_API } from "../../../components/constants";

import type { BankStatementUpload } from "./use-bank-accounts-upload-manager";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60 * 1000;
const MAX_POLL_ATTEMPTS = Math.ceil(POLL_TIMEOUT_MS / POLL_INTERVAL_MS);

export type ExtractionState = "created" | "completed" | "failed";

export interface ExtractionAccount {
  accountType: string;
  bankAccountMask: string | null;
  currency: string;
  statementStartDate: string | null;
  statementEndDate: string | null;
}

export interface ExtractionStatus {
  objectKey: string;
  extractionState: ExtractionState;
  institutionName: string | null;
  businessName: string | null;
  accounts: ExtractionAccount[];
  failureReason: string | null;
}

export interface ExtractionStatusItem {
  upload: BankStatementUpload;
  status: ExtractionStatus | undefined;
  // true once polling has given up without ever seeing a completed/failed state,
  // eg. if the extraction task never runs
  exceededMaxPollTime: boolean;
}

export const useBankStatementExtractionStatuses = (
  customerDomain: string,
  uploads: BankStatementUpload[]
): ExtractionStatusItem[] => {
  const client = useAxios();
  const url = BANK_STATEMENTS_EXTRACTION_STATUS_API.replace(":customerSlug", customerDomain);
  // attempts per upload, counted from queryFn rather than query.state so the render
  // below can see it without reading query internals or wall-clock time
  const [attemptCounts, setAttemptCounts] = useState<Record<string, number>>({});

  const queries = useQueries({
    queries: uploads.map((upload) =>
      queryOptions({
        queryKey: ["bank-statement-extraction-status", customerDomain, upload.objectKey],
        queryFn: async (): Promise<ExtractionStatus> => {
          try {
            const { data } = await client.get<ExtractionStatus>(url, { params: { object_key: upload.objectKey } });
            return data;
          } finally {
            setAttemptCounts((current) => ({ ...current, [upload.objectKey]: (current[upload.objectKey] ?? 0) + 1 }));
          }
        },
        // Avoids inflating attemptCounts via react-query's own retries.
        retry: false,
        refetchInterval: (query) => {
          const state = query.state.data?.extractionState;
          if (state === "completed" || state === "failed") {
            return false;
          }
          if ((attemptCounts[upload.objectKey] ?? 0) >= MAX_POLL_ATTEMPTS) {
            return false;
          }
          return POLL_INTERVAL_MS;
        },
      })
    ),
  });

  return uploads.map((upload, index) => {
    const status = queries[index]?.data;
    const exceededMaxPollTime =
      status?.extractionState !== "completed" &&
      status?.extractionState !== "failed" &&
      (attemptCounts[upload.objectKey] ?? 0) >= MAX_POLL_ATTEMPTS;
    return { upload, status, exceededMaxPollTime };
  });
};
